import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankReconciliationException, BankReconciliationExceptionStatus } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-reconciliation-exceptions',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, SelectModule, InputNumberModule, TableModule, TagModule, TextareaModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Bank Reconciliation" title="Exception Queue" subtitle="Review unmatched, excluded, and unresolved reconciliation exceptions.">
                <div actions>
                    <button pButton type="button" icon="pi pi-refresh" label="Refresh" (click)="loadExceptions()"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-data-panel title="Exceptions" [resultText]="exceptions.length + ' exceptions'">
                <div actions class="toolbar">
                    <p-select [options]="statusOptions" [(ngModel)]="statusFilter" placeholder="Status" [showClear]="true"></p-select>
                    <p-inputNumber [(ngModel)]="bankAccountIdFilter" placeholder="Bank account" [useGrouping]="false"></p-inputNumber>
                    <p-inputNumber [(ngModel)]="sessionIdFilter" placeholder="Session" [useGrouping]="false"></p-inputNumber>
                    <button pButton type="button" icon="pi pi-search" label="Search" [outlined]="true" (click)="loadExceptions()"></button>
                </div>

                <p-table [value]="exceptions" [loading]="loading" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr><th>Exception</th><th>Scope</th><th>Severity</th><th>Status</th><th>Resolution</th><th>Actions</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-exception>
                        <tr>
                            <td>
                                <strong>{{ exception.exceptionType }}</strong>
                                <div class="muted">{{ exception.description || '-' }}</div>
                            </td>
                            <td>
                                <div>Session #{{ exception.sessionId || '-' }}</div>
                                <div class="muted">Line #{{ exception.statementLineId || '-' }} | Bank #{{ exception.bankAccountId }}</div>
                            </td>
                            <td>{{ exception.severity }}</td>
                            <td><p-tag [value]="exception.status" [severity]="severity(exception.status)"></p-tag></td>
                            <td>
                                <textarea pTextarea rows="2" [(ngModel)]="resolutionNotes[exception.exceptionId]" placeholder="Resolution notes"></textarea>
                            </td>
                            <td class="actions">
                                <button pButton type="button" icon="pi pi-folder-open" label="Open" [text]="true" *ngIf="exception.sessionId" (click)="openSession(exception)"></button>
                                <button pButton type="button" icon="pi pi-eye" label="Review" [text]="true" *ngIf="exception.status === 'OPEN'" (click)="review(exception)"></button>
                                <button pButton type="button" icon="pi pi-check" label="Resolve" [text]="true" *ngIf="exception.status === 'OPEN' || exception.status === 'IN_REVIEW'" (click)="resolve(exception)"></button>
                                <button pButton type="button" icon="pi pi-ban" label="Exclude" [text]="true" severity="danger" *ngIf="exception.status === 'OPEN' || exception.status === 'IN_REVIEW'" (click)="exclude(exception)"></button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="6">No reconciliation exceptions match the current filters.</td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .toolbar { display: flex; gap: .5rem; flex-wrap: wrap; align-items: center; }
        .muted { color: #64748b; font-size: .78rem; margin-top: .2rem; }
        .actions { display: flex; gap: .25rem; flex-wrap: wrap; justify-content: flex-end; }
        textarea { width: 100%; min-width: 220px; }
    `]
})
export class BankReconciliationExceptionsComponent implements OnInit {
    exceptions: BankReconciliationException[] = [];
    metrics: PageMetric[] = [];
    resolutionNotes: Record<number, string> = {};
    statusFilter: BankReconciliationExceptionStatus | '' = '';
    bankAccountIdFilter?: number;
    sessionIdFilter?: number;
    loading = false;
    statusOptions: BankReconciliationExceptionStatus[] = ['OPEN', 'IN_REVIEW', 'RESOLVED', 'EXCLUDED'];

    constructor(
        private service: BankReconciliationService,
        private sessionStorageService: SessionStorageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loadExceptions();
    }

    loadExceptions(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        this.loading = true;
        this.service.getExceptions(companyId, {
            status: this.statusFilter,
            bankAccountId: this.bankAccountIdFilter,
            sessionId: this.sessionIdFilter
        }).subscribe({
            next: exceptions => {
                this.exceptions = exceptions ?? [];
                this.metrics = [
                    { label: 'Open', value: this.exceptions.filter(item => item.status === 'OPEN').length, tone: 'warn' },
                    { label: 'In review', value: this.exceptions.filter(item => item.status === 'IN_REVIEW').length, tone: 'info' },
                    { label: 'Resolved', value: this.exceptions.filter(item => item.status === 'RESOLVED').length, tone: 'success' },
                    { label: 'Excluded', value: this.exceptions.filter(item => item.status === 'EXCLUDED').length, tone: 'danger' }
                ];
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    openSession(exception: BankReconciliationException): void {
        if (exception.sessionId) {
            this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', exception.sessionId]);
        }
    }

    review(exception: BankReconciliationException): void {
        this.service.reviewException(exception.exceptionId, this.transition(exception)).subscribe(() => this.loadExceptions());
    }

    resolve(exception: BankReconciliationException): void {
        this.service.resolveException(exception.exceptionId, this.transition(exception)).subscribe(() => this.loadExceptions());
    }

    exclude(exception: BankReconciliationException): void {
        this.service.excludeException(exception.exceptionId, this.transition(exception)).subscribe(() => this.loadExceptions());
    }

    severity(status: BankReconciliationExceptionStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'RESOLVED') return 'success';
        if (status === 'IN_REVIEW') return 'info';
        if (status === 'EXCLUDED') return 'danger';
        return 'warn';
    }

    private transition(exception: BankReconciliationException) {
        return {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            resolutionNotes: this.resolutionNotes[exception.exceptionId],
            comment: this.resolutionNotes[exception.exceptionId]
        };
    }
}
